import { hasJwtFormat, request, setBearerToken } from '../api';
import { createAxiosConfig } from '../project-types/common/axios';
import { HTTP_METHODS } from '../project-types/common/http-types';
import { ApiCreateDonor } from '../project-types/donor/api-types';

export class DonorRegistrationError extends Error {
  constructor(message: string, public credentialsRejected = false) {
    super(message);
    Object.setPrototypeOf(this, DonorRegistrationError.prototype);
  }
}

interface RegistrationFailure {
  code?: string;
  email?: string[];
  user?: string[];
  errors?: { payment?: string[] };
}

function registrationError(data?: RegistrationFailure) {
  if (data?.code === 'REGF-SETPROF-XX-F') {
    return new DonorRegistrationError(
      'This email may already have an account. If it does, the password entered does not match. Use your existing password, or sign in and choose "Forgot password" to reset it.',
      true,
    );
  }
  if (data?.user?.[0] === 'User with this email already exists') {
    return new DonorRegistrationError(
      'An account already exists with this email. Sign in with your existing password, or choose "Forgot password" to reset it.',
      true,
    );
  }
  if (data?.email?.[0]) {
    return new DonorRegistrationError('Please enter a valid email address.', true);
  }
  return new DonorRegistrationError(
    data?.errors?.payment?.[0] || 'We could not complete your registration. Please check your details and try again.',
  );
}

export async function createDonor(
  data: ApiCreateDonor['data'],
): Promise<ApiCreateDonor['successResponse']> {
  let response;
  try {
    response = await request(
      createAxiosConfig<ApiCreateDonor>({
        url: ['/donors'],
        data,
        method: HTTP_METHODS.POST,
      }),
      [200, 400, 401, 403, 422],
    );
  } catch (error) {
    throw registrationError((error as { data?: RegistrationFailure } | undefined)?.data);
  }

  const result = response?.data;
  if (response?.response || result?.status === 'error' || !hasJwtFormat(result?.token)
      || !result?.user || !result?.donorProfile) {
    throw registrationError(response?.response?.data || result);
  }

  setBearerToken(result.token, new Date().toISOString());
  return result;
}
